/**
 * Tenant capability flags — which settings sections and fields
 * actually apply to a given tenant.
 *
 * The archetype is the source of truth, but the settings UI asks
 * questions in behavioural terms ("does PureID host the login
 * page?", "does PureID issue user sessions?") rather than branching
 * on archetype ids in every tab. This helper composes those
 * questions once so every tab consults the same rules and cannot
 * drift.
 *
 * Adding a new capability: add the flag here, update every
 * archetype's row below, and have the relevant settings section
 * gate its rendering on the flag. Do NOT gate directly on
 * archetype id or on tenant_type/user_source/platform_role in
 * the settings pages — that creates N×M drift.
 */

import { deriveArchetype, type ArchetypeId } from "./archetype";
import type { TenantDetail } from "./schemas";

export interface TenantCapabilities {
  /** The archetype id; returned here so callers don't need a
   *  second derivation call for display. */
  archetypeId: ArchetypeId;

  /** True for the platform singleton. Has a narrowed settings
   *  surface. */
  isInfrastructure: boolean;

  /** End-user identity is partitioned by customer organization (CIAM). */
  hasOrganizationIdentityPartitions: boolean;

  /** PureID accepts SCIM 2.0 push from a single upstream IdP.
   *  Only workforce_passkey_layer has this model. */
  hasScimProvisioning: boolean;

  /** An on-prem Active Directory agent (Renegadir-Connect) can push
   *  users/groups into this tenant via the AD-inbound OAuth2
   *  client-credentials surface. Distinct from `hasScimProvisioning`
   *  (generic IdP-agnostic push) — this gates the AD-specific feature.
   *  Same applicability shape as inbound SCIM today: meaningful for
   *  archetypes where an upstream directory feeds users into an
   *  PureID-hosted sign-in surface. */
  supportsInboundDirectorySync: boolean;

  /** Each customer organisation brings its own upstream IdP; PureID
   *  brokers SAML/OIDC per org. Only customer_enterprise_fed. */
  hasPerOrgFederation: boolean;

  /** PureID hosts the user-facing login page (login button, hint
   *  picker, etc.). True when the tenant runs its own login UI
   *  rather than deferring to an upstream. */
  hostsLoginUI: boolean;

  /** PureID runs the passkey enrollment wizard. True except for
   *  customer_enterprise_fed (upstream handles enrollment). */
  hostsEnrollmentUI: boolean;

  /** PureID performs the WebAuthn passkey ceremony itself. True
   *  except for customer_enterprise_fed. */
  performsPasskeyCeremony: boolean;

  /** PureID issues the end-user session token. True for every
   *  shipping archetype today — the only false case is the
   *  infrastructure tenant, which has no end-users. */
  issuesUserSessions: boolean;

  /** PureID sends user-facing emails (recovery, bounces, etc.).
   *  True everywhere except infrastructure (no end-users). */
  sendsUserEmails: boolean;

  /** End-user factor policy (email OTP / TOTP) is meaningful.
   *  False for enterprise_fed (per-org upstream IdP owns factors)
   *  and infrastructure (admins are passkey-only). */
  managesUserFactors: boolean;

  /** Attestation / authenticator-allowlist enforcement is
   *  meaningful. False for enterprise_fed. */
  managesAttestation: boolean;

  /** Supports destructive tenant-level operations (suspend,
   *  delete). False for the infrastructure tenant. */
  allowsDestructiveActions: boolean;
}

/**
 * Resolve capability flags from an authorized tenant detail. Platform
 * inventory intentionally carries no operational classification.
 */
export function tenantCapabilities(
  tenant: Pick<
    TenantDetail,
    | "is_infrastructure"
    | "tenant_type"
    | "user_source"
    | "platform_role"
    | "external_provider"
    | "shape"
  >,
): TenantCapabilities {
  // The label is descriptive, from the sign-in default the tenant was created
  // with; every capability below follows the tenant's SHAPE, the
  // same predicate the feature registry gates on. A workforce tenant with an
  // upstream is a workforce tenant with a connection, and a customer tenant
  // whose people sign in only at their organization's provider is a customer
  // tenant with a reversible sign-in policy; neither is a kind of its own.
  const archetype = deriveArchetype(tenant);
  const shape = tenant.shape;

  if (shape === "infrastructure") {
    return {
      archetypeId: archetype.id,
      isInfrastructure: true,
      hasOrganizationIdentityPartitions: false,
      hasScimProvisioning: false,
      supportsInboundDirectorySync: false,
      hasPerOrgFederation: false,
      hostsLoginUI: false,
      hostsEnrollmentUI: false,
      performsPasskeyCeremony: true, // platform admins use passkeys
      issuesUserSessions: false, // no end-users
      sendsUserEmails: false,
      managesUserFactors: false, // platform admins use passkeys only
      managesAttestation: true, // enforcement applies to admin creds
      allowsDestructiveActions: false,
    };
  }
  if (shape === "workforce") {
    return {
      archetypeId: archetype.id,
      isInfrastructure: false,
      hasOrganizationIdentityPartitions: false,
      // Any workforce tenant may connect a source; the connection is a
      // fact on People → Connections, not a tenant kind.
      hasScimProvisioning: true,
      supportsInboundDirectorySync: true,
      hasPerOrgFederation: false,
      hostsLoginUI: true,
      hostsEnrollmentUI: true,
      performsPasskeyCeremony: true,
      issuesUserSessions: true,
      sendsUserEmails: true,
      managesUserFactors: true,
      managesAttestation: true,
      allowsDestructiveActions: true,
    };
  }
  // Every customer shape: organizations partition identity and each may bring
  // its own provider; whether PureID runs the ceremony for an organization is
  // its sign-in policy (federation-only), read where it applies, never a
  // capability of the tenant.
  return {
    archetypeId: archetype.id,
    isInfrastructure: false,
    hasOrganizationIdentityPartitions: true,
    hasScimProvisioning: false, // an organization's token belongs to the organization
    supportsInboundDirectorySync: false,
    hasPerOrgFederation: true,
    hostsLoginUI: true,
    hostsEnrollmentUI: true,
    performsPasskeyCeremony: true,
    issuesUserSessions: true,
    sendsUserEmails: true,
    managesUserFactors: true,
    managesAttestation: true,
    allowsDestructiveActions: true,
  };
}
